<template>
  <div :class="$style.wrapper">
    <div :class="$style.tools">
      <div :class="$style.dateWrapper">
        <DatePickerRangeInput
          :data-test-id="$testId('datePicker')"
          :minuteIncrement="10"
          :preventFutureDates="true"
          :showValidationMessage="false"
          showTimePicker
          :value="dateTimeRangeValue"
          @input="handleDateTimeChange"
        />
        <CloudInsightsSavedViewsDropdown
          :nonPersonEntityFilter="nonPersonEntityFilter"
          :resetKey="savedViewResetKey"
          :selectedColumns="selectedColumnStrings"
          @resetSavedView="handleResetSavedView"
          @savedViewApply="handleSavedViewApply"
        />
      </div>
      <div :class="$style.manage">
        <DropdownActions
          :class="$style.exportBtn"
          :items="exportOptions"
        >
          <template v-slot:button="dropdown">
            <IconButton
              v-bind="dropdown.defaults"
              :data-test-id="$testId('exportButton')"
              hasNoBackground
              hasOutline
              icon="caretDown"
              iconPosition="right"
              :scale="0.5"
              text="Export"
              type="informational"
              @click="dropdown.defaults.click()"
            />
          </template>
        </DropdownActions>
        <Dropdown
          :class="$style.columnDropdown"
          :data-test-id="$testId('columnDropdown')"
          :isMenuOpen="isColumnsOpen"
          @clickaway="handleColumnsClose"
        >
          <template v-slot:button>
            <IconButton
              :data-test-id="$testId('columnsButton')"
              hasNoBackground
              hasOutline
              icon="caretDown"
              iconPosition="right"
              :scale="0.5"
              text="Columns"
              type="informational"
              @click="handleColumnsClick"
            />
          </template>
          <template v-slot:menu>
            <div
              :class="$style.dropdownContent"
            >
              <div :class="$style.columnsDropdownHeaders">
                <HeadingExtraSmall :class="$style.listLabel">
                  {{ selectedColumnCount }} Selected
                  <span>
                    ({{ maxOptionsSelectable }} max)
                  </span>
                </HeadingExtraSmall>
                <a
                  :class="$style.revertColumnsLink"
                  @click="revertToDefaultColumns"
                >
                  Reset
                </a>
              </div>
              <Listbox
                :class="$style.columnsList"
                :data-test-id="$testId('columnsList')"
                :options="columnOptions"
                :showSearch="true"
                :value="selectedColumnValues"
                @input="handleColumnsChange"
              />
            </div>
          </template>
        </Dropdown>
        <IconButton
          :data-test-id="$testId('manageAccounts')"
          hasOutline
          icon="settings"
          text="Manage Accounts"
          type="secondary"
          @click="handleManageAccountsClick"
        />
      </div>
    </div>
    <div :class="$style.tools">
      <div :class="$style.primaryFilterWrapper">
        <CloudInsightsFilter
          v-for="(filter, index) in primaryFilters"
          :key="index"
          :data-test-id="$testId('primaryFilters')"
          :filter="filter"
          @updateFilters="handleUpdateFilters"
        />
        <ToggleSwitch
          :class="$style.filterCheckbox"
          :data-test-id="$testId('filterCheckbox')"
          label="Hide non-person entity events"
          :value="nonPersonEntityFilter"
          @input="handleNpeChange"
        />
      </div>
      <div :class="$style.filterControls">
        <IconButton
          v-if="showClearAll"
          :class="$style.clearAll"
          :data-test-id="$testId('clearAllButton')"
          hasNoBackground
          hasOutline
          icon="closeCircle"
          iconPosition="left"
          :scale="1"
          text="Clear All"
          type="secondary"
          @click="handleClearAllClick"
        />
        <Dropdown
          :data-test-id="$testId('filterDropdown')"
          :isMenuOpen="isMenuOpen"
          @clickaway="handleFilterClickaway"
        >
          <template v-slot:button>
            <IconButton
              :data-test-id="$testId('filterButton')"
              hasOutline
              icon="filter"
              iconPosition="left"
              :scale="1"
              text="Filter"
              type="secondary"
              @click="handleFilterClick"
            />
          </template>
          <template v-slot:menu>
            <div
              :class="$style.dropdownContent"
            >
              <Listbox
                :class="$style.filterList"
                :data-test-id="$testId('filterList')"
                :options="filterOptions"
                :showSearch="true"
                :value="selectedFilters"
                @input="handleFilterChange"
              />
              <Button
                :class="$style.filterApplyButton"
                :data-test-id="$testId('filterApplyButton')"
                outline
                text="Add Filters"
                type="secondary"
                @click="handleFilterApply"
              />
            </div>
          </template>
        </Dropdown>
      </div>
    </div>
    <div :class="$style.secondaryFilterWrapper">
      <CloudInsightsFilter
        v-for="(filter, index) in secondaryFilters"
        :key="index"
        :class="$style.filterValueDropdown"
        :data-test-id="$testId('secondaryFilters')"
        :filter="filter"
        :showDelete="true"
        @delete="handleDeleteFilter"
        @updateFilters="handleUpdateFilters"
      />
    </div>
    <ProgressModal
      :canCancel="true"
      headerText="Exporting Cloud Insights Events..."
      mainActionText="cancel export"
      :showCount="false"
      @cancel="cancelExport"
    />
  </div>
</template>

<script>
import {
  Button,
  DatePickerRangeInput,
  Dropdown,
  DropdownActions,
  HeadingExtraSmall,
  IconButton,
  Listbox,
  ToggleSwitch,
  Types,
} from '@jumpcloud/ui-components';
import {
  ExportFileTypes,
  ItemTypeConsts,
  LocalStorageConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  additionalColumnsData,
  columnDataMap,
  defaultColumnsData,
  optionalFilters,
  primaryFilters,
  routeConsts,
  routeNames,
} from 'client/CloudInsights/constants/CloudInsightsConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import CloudInsightsFilter from 'client/CloudInsights/CloudInsightsItemList/CloudInsightsFilter.vue';
import CloudInsightsSavedViewsDropdown from 'client/CloudInsights/CloudInsightsItemList/CloudInsightsSavedViewsDropdown.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import ProgressModal from 'components/Modal/ProgressModal.vue';

const { cloudInsightsEventsSelectedColumns } = LocalStorageConsts;
const { DropdownMenuItem, ListboxItem } = Types;
const cloudInsightsEventsModule = StoreModuleMap[ItemTypeConsts.cloudInsightsEvents];
const defaultColumnNames = defaultColumnsData.map(columnConfig => columnConfig.label);

export default {
  name: 'CloudInsightsItemListToolbarV2',

  components: {
    Button,
    CloudInsightsFilter,
    CloudInsightsSavedViewsDropdown,
    DatePickerRangeInput,
    Dropdown,
    DropdownActions,
    HeadingExtraSmall,
    IconButton,
    Listbox,
    ProgressModal,
    ToggleSwitch,
  },

  LocalStorageService,

  props: {
    rowsPerPage: {
      type: Number,
      default: 10,
    },
    selectedColumns: {
      type: Array,
      default: () => ([]),
    },
    totalEvents: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      isColumnsOpen: false,
      isMenuOpen: false,
      maxOptionsSelectable: 8,
      savedViewResetKey: 0,
      selectedFilters: [],
    };
  },

  computed: {
    ...mapState(cloudInsightsEventsModule, [
      'nonPersonEntityFilter',
      'selectedSavedView',
    ]),
    ...mapGetters(cloudInsightsEventsModule, [
      'getCollection',
      'getDateTimeRange',
      'getFilters',
    ]),

    areColumnsDefault() {
      const columnStrings = defaultColumnsData
        .filter((col) => (col.label !== '')) // Remove collapse column
        .map((col) => (col.label));

      return (this.selectedColumnStrings.length === columnStrings.length)
        && this.selectedColumnStrings.every((col, index) => (col === columnStrings[index]));
    },

    areFiltersApplied() {
      let areFiltersApplied = false;
      const appliedFilters = this.getFilters;
      const primaryFilterFields = this.primaryFilters.map((filter) => (filter.value));

      appliedFilters.forEach((filter) => {
        // If the applied filter is a primary filter, check if a value exists
        if (primaryFilterFields.indexOf(filter.field) > -1) {
          if (filter.values.length) areFiltersApplied = true;
        } else {
          // If the applied filter is not a primary, a secondary filter has been applied
          areFiltersApplied = true;
        }
      });
      return areFiltersApplied;
    },

    columnOptions() {
      const disabledFields = ['Date'];
      const defaultColumns = defaultColumnsData
        .map(column => (column.label));
      const additionalColumns = additionalColumnsData
        .map(column => (column.label))
        .sort();
      const columns = [
        // Put default columns at the top of list
        ...defaultColumns,
        ...additionalColumns,
      ];
      const selected = this.selectedColumns.map(col => (col.label));
      return columns
        .filter(field => (field !== '')) // Remove collapse column
        .map(field => new ListboxItem({
          name: field,
          value: field,
          disabled: (!selected.includes(field) && this.isColumnsDisabled)
            || disabledFields.includes(field),
        }));
    },

    dateTimeRangeValue() {
      return this.getDateTimeRange;
    },

    exportOptions() {
      return [
        new DropdownMenuItem({
          click: () => this.exportEvents(ExportFileTypes.json),
          text: 'Export JSON',
        }),
        new DropdownMenuItem({
          click: () => this.exportEvents(ExportFileTypes.csv),
          text: 'Export CSV',
        }),
      ];
    },

    filterOptions() {
      return optionalFilters
        .map(filter => new ListboxItem({ name: filter.label, value: filter.dataFieldName }));
    },

    isColumnsDisabled() {
      return this.selectedColumnCount >= this.maxOptionsSelectable;
    },

    primaryFilters() {
      return primaryFilters
        .map(filter => new ListboxItem({ name: filter.label, value: filter.dataFieldName }));
    },

    secondaryFilters() {
      const primaryFilterNames = primaryFilters.map((p) => (p.dataFieldName));
      const secondaryFilters = this.getFilters.reduce((acc, filter) => {
        if (primaryFilterNames.indexOf(filter.field) === -1 && acc.indexOf(filter.field) === -1) {
          const { label } = columnDataMap[filter.field];
          acc.push({ name: label, value: filter.field });
        }
        return acc;
      }, []);

      return secondaryFilters;
    },

    selectedColumnCount() {
      // Subtract 1 for the expand row button column
      return this.selectedColumns.length - 1;
    },

    selectedColumnStrings() {
      return this.selectedColumns
        .filter((col) => (col.label !== '')) // Remove collapse column
        .map((col) => (col.label));
    },

    selectedColumnValues() {
      const disabledFields = ['Date'];
      return this.selectedColumns
        .map(column => new ListboxItem({
          name: column.label,
          value: column.label,
          disabled: disabledFields.includes(column.label),
        }));
    },

    showClearAll() {
      let showClearAll = false;

      if (!this.areColumnsDefault) showClearAll = true;

      if (this.areFiltersApplied) showClearAll = true;

      if (this.selectedSavedView) showClearAll = true;

      if (this.nonPersonEntityFilter) showClearAll = true;

      return showClearAll;
    },
  },

  methods: {
    ...mapActions(cloudInsightsEventsModule, [
      'addFilter',
      'cancelExport',
      'deleteFilter',
      'exportData',
      'fetchCollection',
      'setDateTimeRange',
      'setFilter',
      'setLoadingState',
      'setNonPersonEntityFilter',
      'setPage',
      'setSearchTerm',
      'setSelectedSavedView',
      'resetCollection',
      'resetFilters',
    ]),
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
    ]),

    async exportEvents(exportType) {
      try {
        await this.exportData(exportType);
      } catch {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'Unable to export events.',
        });
      }
    },

    handleClearAllClick() {
      this.savedViewResetKey += 1;
      this.handleResetSavedView();
    },

    handleColumnsChange(selectedColumns) {
      const formattedColumns = selectedColumns.map(column => (column.value));
      if (!formattedColumns.includes('')) {
        formattedColumns.unshift('');
      }
      const { setItem } = this.$options.LocalStorageService;
      // Add collapse column back to array
      setItem(cloudInsightsEventsSelectedColumns, JSON.stringify(formattedColumns));
      this.$emit('columnsChange');
    },

    handleColumnsClick() {
      this.isColumnsOpen = !this.isColumnsOpen;
    },

    handleColumnsClose() {
      this.isColumnsOpen = false;
    },

    async handleDateTimeChange(dateTimeRange) {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });
      this.setDateTimeRange({ dateTimeRange });
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
    },

    async handleDeleteFilter(filter) {
      this.resetCollection({ soft: true });
      this.deleteFilter(filter.value);
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      // Make filter dropdown reflect change
      const updatedArray = this.selectedFilters
        .filter((selected) => (selected.value !== filter.value));
      this.selectedFilters = updatedArray;
    },

    handleFilterApply() {
      this.selectedFilters.forEach(filter => {
        const field = filter.value;
        this.addFilter(field);
      });
      this.handleFilterClose();
    },

    handleFilterChange(filters) {
      this.selectedFilters = filters;
    },

    handleFilterClick() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    handleFilterClickaway() {
      this.selectedFilters = this.secondaryFilters;
      this.handleFilterClose();
    },

    handleFilterClose() {
      this.isMenuOpen = false;
    },

    handleManageAccountsClick() {
      this.$router.push({ name: routeNames[routeConsts.cloudInsightsAccounts] });
    },

    async handleNpeChange(value) {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });
      this.setNonPersonEntityFilter({ value });
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
    },

    async handleResetSavedView() {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });
      this.resetFilters();
      this.setSelectedSavedView('');
      this.revertToDefaultColumns();
      this.setNonPersonEntityFilter({ value: false });
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
      this.selectedFilters = [];
    },

    async handleSavedViewApply({
      columns = [],
      filters = [],
      excludeNonPersonEntities = false,
      viewId = '',
    }) {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });

      this.setNonPersonEntityFilter({ value: excludeNonPersonEntities });
      this.setSelectedSavedView(viewId);

      if (columns.length) {
        const formattedColumns = columns.map(col => ({ value: col }));
        this.handleColumnsChange(formattedColumns);
      }

      this.resetFilters();
      if (filters.length) {
        filters.forEach((filter) => {
          this.setFilter({ field: filter.field, values: filter.values });
        });
      }

      // Set the selected values for filter dropdown
      const selectedFilters = filters.map((filter) => ({
        name: columnDataMap[filter.field].label,
        value: filter.field,
      }));
      this.selectedFilters = selectedFilters;

      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
    },

    async handleUpdateFilters({ field, values = [] }) {
      this.setLoadingState(true);
      this.resetCollection({ soft: true });
      this.setFilter({ field, values });
      this.setPage({ pageNumber: 0, limit: this.rowsPerPage });
      await this.fetchCollection();
      this.setLoadingState(false);
    },

    revertToDefaultColumns() {
      const { setItem } = this.$options.LocalStorageService;
      setItem(cloudInsightsEventsSelectedColumns, JSON.stringify(defaultColumnNames));
      this.$emit('columnsChange');
    },
  },
};
</script>

<style module scoped>
.clearAll {
  padding-left: var(--jc-spacer-small);
}

.columnDropdown {
  margin-right: var(--jc-spacer-small);
}

.columnSelector {
  border: 2px solid var(--jcBlack);
  border-radius: var(--jc-border-radius-small);
  cursor: pointer;
  margin-right: var(--jc-spacer-small);
}

.columnSelector /deep/ button {
  color: black;
  height: 28px;
  padding: 4px 8px;
  width: 90px;
}

.columnSelector:hover {
  background-color: var(--jcGrey200);
}

.dropdownContent {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: var(--jc-border-radius);
  margin: var(--jc-spacer-large) 0;
  padding: 16px;
  position: absolute;
  right: 0;
  top: 8px;
  z-index: 7;
}

.exportBtn {
  margin-right: var(--jc-spacer-small);
  z-index: 7;
}

.exportBtn /deep/ ul {
  width: 135px;
}

.filterApplyButton {
  width: 100%;
}

.secondaryFilterWrapper {
  display: flex;
  flex-wrap: wrap;
}

.filterList {
  margin-bottom: 20px;
}

.manage {
  align-items: center;
  display: inline-flex;
  gap: var(--jc-spacer);
}

.columnsDropdownHeaders {
  display: flex;
  justify-content: space-between;
}

.revertColumnsLink { cursor: pointer; }

.filterValueDropdown {
  margin-bottom: var(--jc-spacer-small);
}

.dateWrapper,
.primaryFilterWrapper {
  align-items: center;
  display: inline-flex;
}

.primaryFilterWrapper {
  z-index: 6 !important;
}

.tools {
  display: flex;
  justify-content: space-between;
  padding-bottom: var(--jc-spacer-small);
}

.tools:last-child {
  padding-bottom: 0;
}

.filterControls {
  display: flex;
  gap: var(--jc-spacer);
}

.wrapper {
  padding: 16px 16px 0 0;
}
</style>
